import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Image,
  useDisclosure,
  Spinner,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Icon,
  IconButton,
} from '@chakra-ui/react';
import {
  Table,
  TableHead,
  TableHeaderSortCell,
  TableSortDirection,
  TableBody,
  TableRow,
  TableCell,
  HeadingText,
  Button,
  showToast,
} from '@spotoninc/nexus-react';
import {
  AddIcon,
  MoreVerticalIcon,
  EditIcon,
  DeleteIcon,
} from '@spotoninc/nexus-web-assets';
import PrivilegeImage from '../../media/images/PrivilegeImage.svg';
import { fetchJsonWithAuth } from '../../utils/apiHelpers';
import AddOrEditPrivilegeModal from './addOrEditPrivilegeModal';
import LocationModal from './locationModal';
import ConfirmationDialog from '../manageRoles/ConfirmationDialog';

const ManagePrivilege = () => {
  const [privileges, setPrivileges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingPrivilegeId, setEditingPrivilegeId] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'description',
    direction: TableSortDirection.ascending,
  });
  const [selectedPrivilege, setSelectedPrivilege] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [privilegeName, setPrivilegeName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [locations, setLocations] = useState({});
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState({});
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [currentIntegration, setCurrentIntegration] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [privilegeToDelete, setPrivilegeToDelete] = useState(null);
  const [prevLocationSelections, setPrevLocationSelections] = useState({});
  const [prevAllIntegrationIds, setPrevAllIntegrationIds] = useState([]);

  // Fetch privileges data
  const fetchPrivileges = async () => {
    try {
      setLoading(true);

      const data = await fetchJsonWithAuth(`/api/get_priv_data/null/`);

      // Get the initial privileges data
      const initialPrivileges = data.priv_data || [];

      // Set integrations data
      let integrationsData = [];
      if (data.integrations && Array.isArray(data.integrations)) {
        integrationsData = data.integrations;
        setIntegrations(integrationsData);
      }

      // Set locations data
      const locationsMap = {};
      Object.keys(data).forEach(key => {
        // Check if the key is a number (integration ID) and the value is an array
        if (/^\d+$/.test(key) && Array.isArray(data[key])) {
          locationsMap[key] = data[key];
        }
      });
      setLocations(locationsMap);

      // Create a map of location ids to their integration names
      const locationToIntegrationMap = {};
      Object.entries(locationsMap).forEach(([integrationId, locationsList]) => {
        const integration = integrationsData.find(
          i => i.id === parseInt(integrationId)
        );
        const integrationName = integration
          ? integration.app_name
          : 'Unknown Integration';

        locationsList.forEach(location => {
          locationToIntegrationMap[location.location_id] = {
            integrationId: parseInt(integrationId),
            integrationName,
            locationName: location.description || 'Unknown Location',
          };
        });
      });

      // If there are privileges, fetch detailed data for each one
      if (initialPrivileges.length > 0) {
        const detailedPrivilegesPromises = initialPrivileges.map(
          async privilege => {
            try {
              const detailData = await fetchJsonWithAuth(
                `/api/get_priv_data/${privilege.priviledge_id}/`
              );

              // Build assigned integration IDs as union of:
              // - explicit ALL integrations from assigned_integrations
              // - integrations that have any assigned locations in detailData
              const assignedIdSet = new Set();
              if (Array.isArray(detailData.assigned_integrations)) {
                detailData.assigned_integrations.forEach(item => {
                  if (
                    item &&
                    typeof item === 'object' &&
                    item.integration_id_id
                  ) {
                    const id =
                      typeof item.integration_id_id === 'string'
                        ? parseInt(item.integration_id_id, 10)
                        : item.integration_id_id;
                    if (!Number.isNaN(id)) assignedIdSet.add(id);
                  } else {
                    const id =
                      typeof item === 'string' ? parseInt(item, 10) : item;
                    if (!Number.isNaN(id)) assignedIdSet.add(id);
                  }
                });
              }

              Object.keys(detailData).forEach(key => {
                if (/^\d+$/.test(key) && Array.isArray(detailData[key])) {
                  const intId = parseInt(key, 10);
                  const hasAssigned = detailData[key].some(loc => loc.assigned);
                  if (hasAssigned) assignedIdSet.add(intId);
                }
              });

              // Map integration IDs to their names
              const integrationNames = [...assignedIdSet]
                .map(id => {
                  const integration = integrationsData.find(i => i.id === id);
                  return integration ? integration.app_name : null;
                })
                .filter(Boolean);

              // Get all assigned locations with their names
              const assignedLocationNames = [];

              // Process each integration's locations
              Object.keys(detailData).forEach(key => {
                if (!isNaN(key) && Array.isArray(detailData[key])) {
                  const integrationId = parseInt(key);
                  const integration = integrationsData.find(
                    i => i.id === integrationId
                  );
                  const integrationName = integration
                    ? integration.app_name
                    : 'Unknown Integration';

                  // Filter to assigned locations only and map to location names
                  const assignedLocations = detailData[key]
                    .filter(location => location.assigned)
                    .map(location => {
                      return {
                        id: location.location_id,
                        name: location.description || 'Unknown Location',
                        integrationName,
                      };
                    });

                  assignedLocationNames.push(
                    ...assignedLocations.map(
                      loc => `${loc.name} (${loc.integrationName})`
                    )
                  );
                }
              });

              // Return the privilege with additional detail data
              return {
                ...privilege,
                assigned_integrations: integrationNames,
                assigned_locations: assignedLocationNames,
              };
            } catch (error) {
              console.error(
                `Error fetching details for privilege ${privilege.priviledge_id}:`,
                error
              );
              // Return the original privilege data if detail fetch fails
              return privilege;
            }
          }
        );

        // Wait for all detail requests to complete
        const detailedPrivileges = await Promise.all(
          detailedPrivilegesPromises
        );

        // Set the privileges with detailed data
        setPrivileges(detailedPrivileges);
      } else {
        // No privileges to fetch details for
        setPrivileges(initialPrivileges);
      }
    } catch (error) {
      console.error('Error fetching privileges:', error);
      showToast({
        priority: 'error',
        content: 'Failed to load privileges',
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch privilege details for editing
  const fetchPrivilegeDetails = async privilegeId => {
    try {
      setLoading(true);
      const data = await fetchJsonWithAuth(
        `/api/get_priv_data/${privilegeId}/`,
        {
          method: 'GET',
        }
      );

      return data;
    } catch (error) {
      console.error('Error fetching privilege details:', error);
      showToast({
        priority: 'error',
        content: 'Failed to load privilege details',
        autoClose: 5000,
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (key, newDirection) => {
    setSortConfig({ key, direction: newDirection });
  };

  // Format date to MM/DD/YYYY
  const formatDate = dateString => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  // Format integrations display
  const formatIntegrations = integrations => {
    if (!integrations?.length) return '--';

    return integrations.length === 1
      ? integrations[0]
      : `${integrations[0]} + ${integrations.length - 1} ${integrations.length === 2 ? 'Other' : 'Others'}`;
  };

  // Sort data
  const sortedData = React.useMemo(() => {
    let sortableData = [...privileges];
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle array fields (assigned_integrations, assigned_locations)
        if (
          sortConfig.key === 'assigned_integrations' ||
          sortConfig.key === 'assigned_locations'
        ) {
          // For arrays, compare the first item or empty string if array is empty
          aValue = aValue && aValue.length > 0 ? aValue[0] : '';
          bValue = bValue && bValue.length > 0 ? bValue[0] : '';

          // Convert to lowercase for case-insensitive alphabetical sorting
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }
        }
        // Handle date fields
        else if (sortConfig.key === 'created_date') {
          // Convert to Date objects for proper date comparison
          aValue = aValue ? new Date(aValue) : new Date(0); // Use epoch for null/undefined dates
          bValue = bValue ? new Date(bValue) : new Date(0);
        }
        // Handle text fields (description/privilege name)
        else if (sortConfig.key === 'description') {
          // Convert to lowercase for case-insensitive alphabetical sorting
          aValue = aValue ? aValue.toLowerCase() : '';
          bValue = bValue ? bValue.toLowerCase() : '';
        }
        // Handle numeric fields
        else if (typeof aValue === 'number' && typeof bValue === 'number') {
          // Numbers are compared directly (no conversion needed)
        }
        // Handle other string fields
        else if (typeof aValue === 'string' && typeof bValue === 'string') {
          // Convert to lowercase for case-insensitive alphabetical sorting
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        // Perform the comparison
        if (aValue < bValue) {
          return sortConfig.direction === TableSortDirection.ascending ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === TableSortDirection.ascending ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [privileges, sortConfig]);

  // Handle add privilege
  const handleAddPrivilege = () => {
    setSelectedPrivilege(null);
    setPrivilegeName('');
    setSelectedIntegrations([]);
    setSelectedLocations({});
    onOpen();
  };

  // Handle edit privilege
  const handleEditPrivilege = async privilege => {
    try {
      setEditLoading(true);
      setEditingPrivilegeId(privilege.priviledge_id);

      const privilegeDetails = await fetchJsonWithAuth(
        `/api/get_priv_data/${privilege.priviledge_id}/`
      );

      if (privilegeDetails) {
        setSelectedPrivilege(privilege);
        setPrivilegeName(privilege.description);

        // Set selected locations
        const locationSelections = {};

        // Process each integration's locations from the API response
        Object.keys(privilegeDetails).forEach(key => {
          // Check if the key is a number (integration ID) and the value is an array
          if (/^\d+$/.test(key) && Array.isArray(privilegeDetails[key])) {
            const integrationId = parseInt(key);

            // Filter only assigned locations
            const assignedLocations = privilegeDetails[key]
              .filter(location => location.assigned)
              .map(location => location.location_id);

            if (assignedLocations.length > 0) {
              locationSelections[integrationId] = assignedLocations;
            }
          }
        });

        setSelectedLocations(locationSelections);

        // Determine selected integrations for edit
        // Prefer top-level assigned_integrations which includes integrations even with no locations
        let selectedInts = [];
        if (Array.isArray(privilegeDetails.assigned_integrations)) {
          selectedInts = privilegeDetails.assigned_integrations
            .map(item => {
              if (item && typeof item === 'object') {
                const raw =
                  'integration_id_id' in item
                    ? item.integration_id_id
                    : 'integration_id' in item
                      ? item.integration_id
                      : null;
                const val = typeof raw === 'string' ? parseInt(raw, 10) : raw;
                return Number.isNaN(val) ? null : val;
              }
              const val = typeof item === 'string' ? parseInt(item, 10) : item;
              return Number.isNaN(val) ? null : val;
            })
            .filter(v => v !== null);
        } else if (
          privilegeDetails.priv_data &&
          Array.isArray(privilegeDetails.priv_data)
        ) {
          // Fallback: extract from the matching priv item
          const privItem = privilegeDetails.priv_data.find(
            item => item.priviledge_id === privilege.priviledge_id
          );
          if (privItem && Array.isArray(privItem.assigned_integrations)) {
            selectedInts = privItem.assigned_integrations
              .map(item => {
                // Handle both object shape {integration_id_id} and primitive id
                if (
                  item &&
                  typeof item === 'object' &&
                  'integration_id_id' in item
                ) {
                  return typeof item.integration_id_id === 'string'
                    ? parseInt(item.integration_id_id, 10)
                    : item.integration_id_id;
                }
                return typeof item === 'string' ? parseInt(item, 10) : item;
              })
              .filter(id => id !== null && !Number.isNaN(id));
            // Remove duplicates
            selectedInts = [...new Set(selectedInts)];
          }
        }
        // Preserve previous assignments for diffing on save
        setPrevLocationSelections(locationSelections);
        const explicitSubsetIds = Object.keys(locationSelections).map(id =>
          parseInt(id, 10)
        );
        const prevAll = (selectedInts || []).filter(
          id => !explicitSubsetIds.includes(id)
        );
        setPrevAllIntegrationIds(prevAll);

        // Ensure integrations that have any assigned locations remain checked
        const unionSelected = Array.from(
          new Set([...(selectedInts || []), ...explicitSubsetIds])
        );
        setSelectedIntegrations(unionSelected);

        onOpen();
      }
    } catch (error) {
      console.error('Error in handleEditPrivilege:', error);
      showToast({
        priority: 'error',
        content: 'Failed to load privilege details',
        autoClose: 5000,
      });
    } finally {
      setEditLoading(false);
      setEditingPrivilegeId(null);
    }
  };

  // Handle integration selection
  const handleIntegrationChange = (integrationId, isChecked) => {
    if (isChecked) {
      // Add the integration to selectedIntegrations if it's not already there
      setSelectedIntegrations(prev => {
        if (prev.includes(integrationId)) {
          return prev;
        }
        return [...prev, integrationId];
      });
    } else {
      // Remove the integration from selectedIntegrations
      setSelectedIntegrations(prev => prev.filter(id => id !== integrationId));

      // Also remove any selected locations for this integration
      setSelectedLocations(prev => {
        const newSelections = { ...prev };
        delete newSelections[integrationId];
        return newSelections;
      });
    }
  };

  // Open location modal for an integration
  const handleOpenLocationModal = integrationId => {
    setCurrentIntegration(integrationId);
    setLocationModalOpen(true);
  };

  // Handle location selection
  const handleLocationChange = (locationId, isChecked) => {
    if (!currentIntegration) return;

    // Update selected locations
    setSelectedLocations(prev => {
      const newSelections = { ...prev };

      if (!newSelections[currentIntegration]) {
        newSelections[currentIntegration] = [];
      }

      if (isChecked) {
        newSelections[currentIntegration] = [
          ...newSelections[currentIntegration],
          locationId,
        ];

        // We no longer automatically add the integration to selectedIntegrations
        // when locations are selected - this is controlled by the API response
      } else {
        newSelections[currentIntegration] = newSelections[
          currentIntegration
        ].filter(id => id !== locationId);

        // Remove the integration key if no locations are selected
        if (newSelections[currentIntegration].length === 0) {
          delete newSelections[currentIntegration];
        }
      }

      return newSelections;
    });
  };

  // Close location modal
  const handleCloseLocationModal = () => {
    // We no longer automatically add integrations with locations to selectedIntegrations
    setLocationModalOpen(false);
    setCurrentIntegration(null);
  };

  // Go back from location modal to privilege modal
  const handleBackToPrivilegeModal = () => {
    // We no longer automatically add integrations with locations to selectedIntegrations
    setLocationModalOpen(false);
    setCurrentIntegration(null);
  };

  // Handle delete privilege
  const handleDeletePrivilege = async privilegeId => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('privId', privilegeId);
      formData.append('type', 'delete');

      await fetchJsonWithAuth(`/api/set_priv_data/`, {
        method: 'POST',
        body: formData,
      });

      // Refresh privileges data
      fetchPrivileges();

      showToast({
        priority: 'success',
        content: 'Privilege deleted successfully',
        autoClose: 5000,
      });
    } catch (error) {
      console.error('Error deleting privilege:', error);
      showToast({
        priority: 'error',
        content: 'Failed to delete privilege',
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = privilegeId => {
    setPrivilegeToDelete(privilegeId);
    setIsDeleteDialogOpen(true);
  };

  // Handle submit privilege (add or edit)
  const handleSubmitPrivilege = async () => {
    try {
      if (!privilegeName.trim()) {
        showToast({
          priority: 'error',
          content: 'Privilege name cannot be empty',
          autoClose: 5000,
        });
        return;
      }

      setIsSubmitting(true);

      const formData = new FormData();
      formData.append('privName', privilegeName);
      formData.append('type', 'edit');

      if (selectedPrivilege) {
        formData.append('privId', selectedPrivilege.priviledge_id);
      } else {
        formData.append('privId', '');
      }

      // Compute diffs vs previous state and send explicit adds/removes
      const toInt = v => (typeof v === 'string' ? parseInt(v, 10) : v);
      const allLocIdsByIntegration = {};
      Object.keys(locations || {}).forEach(integrationKey => {
        const intId = parseInt(integrationKey, 10);
        allLocIdsByIntegration[intId] = (locations[integrationKey] || []).map(
          l => toInt(l.location_id)
        );
      });

      const prevAllSet = new Set(prevAllIntegrationIds || []);
      const desiredSelections = selectedLocations || {};

      integrations.forEach(integration => {
        const intId = toInt(integration.id);
        const allLocs = allLocIdsByIntegration[intId] || [];

        const prevSet = new Set(
          prevAllSet.has(intId) ? allLocs : prevLocationSelections[intId] || []
        );

        const isSelected = selectedIntegrations.includes(intId);
        const subset = desiredSelections[intId] || [];
        const newSet = new Set(
          isSelected ? (subset.length > 0 ? subset : allLocs) : []
        );

        const adds = [...newSet].filter(locationId => !prevSet.has(locationId));
        const removes = [...prevSet].filter(
          locationId => !newSet.has(locationId)
        );

        adds.forEach(locationId => {
          const key = `P_${locationId}_${intId}${selectedPrivilege ? `_${selectedPrivilege.priviledge_id}` : ''}`;
          formData.append(key, 'true');
        });
        removes.forEach(locationId => {
          const key = `P_${locationId}_${intId}${selectedPrivilege ? `_${selectedPrivilege.priviledge_id}` : ''}`;
          formData.append(key, 'false');
        });

        const assignAll =
          isSelected && (subset.length === 0 || newSet.size === allLocs.length);
        // Keep integration mapping if either all locations are assigned OR specific locations are selected
        const hasLocationSelections = newSet.size > 0;
        formData.append(
          `I_${intId}`,
          assignAll || hasLocationSelections ? 'true' : 'false'
        );
      });

      await fetchJsonWithAuth(`/api/set_priv_data/`, {
        method: 'POST',
        body: formData,
      });

      // Keep modal open and show spinner until the table is refreshed
      await fetchPrivileges();
      onClose();

      showToast({
        priority: 'success',
        content: selectedPrivilege
          ? 'Privilege updated successfully'
          : 'Privilege added successfully',
        autoClose: 5000,
      });
    } catch (error) {
      console.error('Error submitting privilege:', error);
      showToast({
        priority: 'error',
        content: 'Failed to save privilege',
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch privileges on component mount
  useEffect(() => {
    fetchPrivileges();
  }, []);

  return (
    <Box
      ml={{ base: '16px', md: '40px' }}
      mr={{ base: '16px', md: '72px' }}
      py={10}
    >
      <Flex
        justify={{ base: 'flex-start', md: 'space-between' }}
        align={{ base: 'flex-start', md: 'center' }}
        mb={8}
        direction={{ base: 'column', md: 'row' }}
      >
        <HeadingText variant="heading2">Manage Privileges</HeadingText>
        <Button
          onClick={handleAddPrivilege}
          leadingIcon={AddIcon}
          mt={{ base: 4, md: 0 }}
        >
          Add Privilege
        </Button>
      </Flex>

      {loading && privileges.length === 0 ? (
        <Flex justify="center" align="center" height="300px">
          <Spinner size="xl" />
        </Flex>
      ) : privileges.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={{ base: 6, md: 10 }}
          px={4}
          minH="300px"
        >
          <Box mb={4} mt={8}>
            <Image src={PrivilegeImage} alt="Privilege" />
          </Box>
          <HeadingText size="small" mb={2} textAlign="center">
            Add your first privilege
          </HeadingText>
          <Text
            textAlign="center"
            color="gray.600"
            maxW="450px"
            fontSize={{ base: 'sm', md: 'md' }}
          >
            Create privileges to control access to specific features and data.
          </Text>
        </Flex>
      ) : (
        <Box overflow="hidden" bg="white">
          <Box overflowX="auto">
            <Table>
              <TableHead>
                <TableRow variant="filled">
                  <TableHeaderSortCell
                    onSort={direction => handleSort('description', direction)}
                    direction={
                      sortConfig.key === 'description'
                        ? sortConfig.direction
                        : undefined
                    }
                  >
                    Privilege Name
                  </TableHeaderSortCell>
                  <TableHeaderSortCell
                    onSort={direction =>
                      handleSort('assigned_integrations', direction)
                    }
                    direction={
                      sortConfig.key === 'assigned_integrations'
                        ? sortConfig.direction
                        : undefined
                    }
                  >
                    Assigned Integrations
                  </TableHeaderSortCell>
                  <TableHeaderSortCell
                    onSort={direction =>
                      handleSort('assigned_locations', direction)
                    }
                    direction={
                      sortConfig.key === 'assigned_locations'
                        ? sortConfig.direction
                        : undefined
                    }
                  >
                    Assigned Locations
                  </TableHeaderSortCell>
                  <TableHeaderSortCell
                    onSort={direction => handleSort('created_date', direction)}
                    direction={
                      sortConfig.key === 'created_date'
                        ? sortConfig.direction
                        : undefined
                    }
                  >
                    Created
                  </TableHeaderSortCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map(privilege => (
                  <TableRow
                    key={privilege.priviledge_id}
                    variant="bottom-divider"
                    bg={
                      editLoading &&
                      editingPrivilegeId === privilege.priviledge_id
                        ? 'blue.50'
                        : 'white'
                    }
                  >
                    <TableCell>
                      <Flex align="center">
                        {editLoading &&
                        editingPrivilegeId === privilege.priviledge_id ? (
                          <Spinner size="sm" color="blue.500" mr={2} />
                        ) : null}
                        <Text>{privilege.description}</Text>
                      </Flex>
                    </TableCell>
                    <TableCell>
                      <Text>
                        {formatIntegrations(privilege.assigned_integrations)}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Text>
                        {formatIntegrations(privilege.assigned_locations)}
                      </Text>
                    </TableCell>
                    <TableCell>
                      <Flex align="center" justify="space-between">
                        <Text>{formatDate(privilege.created_date)}</Text>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<MoreVerticalIcon />}
                            variant="ghost"
                            borderRadius="full"
                            size="sm"
                          />
                          <MenuList borderRadius="md" bg="gray.100">
                            <MenuItem
                              height="48px"
                              bg="gray.100"
                              icon={<Icon as={EditIcon} w="24px" h="24px" />}
                              onClick={() => handleEditPrivilege(privilege)}
                              _hover={{ bg: '#D3E5FF' }}
                              borderRadius="md"
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              height="48px"
                              bg="gray.100"
                              icon={<Icon as={DeleteIcon} w="24px" h="24px" />}
                              onClick={() =>
                                openDeleteDialog(privilege.priviledge_id)
                              }
                              _hover={{ bg: '#D3E5FF' }}
                              borderRadius="md"
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Flex>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {/* Add/Edit Privilege Modal */}
      <AddOrEditPrivilegeModal
        isOpen={isOpen && !locationModalOpen}
        onClose={onClose}
        selectedPrivilege={selectedPrivilege}
        privilegeName={privilegeName}
        setPrivilegeName={setPrivilegeName}
        integrations={integrations}
        locations={locations}
        selectedIntegrations={selectedIntegrations}
        handleIntegrationChange={handleIntegrationChange}
        handleOpenLocationModal={handleOpenLocationModal}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmitPrivilege}
        selectedLocations={selectedLocations}
      />

      {/* Location Selection Modal */}
      <LocationModal
        isOpen={locationModalOpen}
        onClose={handleCloseLocationModal}
        onBack={handleBackToPrivilegeModal}
        currentIntegration={currentIntegration}
        locations={locations}
        selectedLocations={selectedLocations}
        handleLocationChange={handleLocationChange}
      />

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={() => handleDeletePrivilege(privilegeToDelete)}
        title="Delete Privilege"
        body="Deleting this privilege cannot be undone."
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        confirmButtonColor="#D54041"
      />
    </Box>
  );
};

export default ManagePrivilege;
